import React, { useMemo, useState, useEffect } from 'react';
import { previewSocial } from '../utils/socials';
import { userAPI } from '../services/api';

const VISIBILITIES = ['public', 'friends', 'private'];

const SocialSettings = ({ socials = [] }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [socialToRemove, setSocialToRemove] = useState(null);
  const [platform, setPlatform] = useState('telegram');
  const [value, setValue] = useState('');
  const [visibility, setVisibility] = useState('friends');
  const [preview, setPreview] = useState({ link: '', error: '' });
  const [message, setMessage] = useState({ text: '', type: '' }); // 'success' or 'error'
  
  // Loading states
  const [updateSocialLoading, setUpdateSocialLoading] = useState(false);
  


  // Debounced preview calculation
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const p = previewSocial(platform, value);
        setPreview({ link: p.link, error: '' });
      } catch (e) {
        setPreview({ link: '', error: e?.message || 'Invalid value' });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [platform, value]);

  const placeholder = useMemo(() => {
    if (platform === 'telegram') return '@username';
    return 'username';
  }, [platform]);

  const openDialog = () => {
    setShowDialog(true);
    setMessage({ text: '', type: '' }); // Clear any previous messages
  };

  const closeDialog = () => {
    setShowDialog(false);
  };



  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setUpdateSocialLoading(true);
      const payload = { platform, value, visibility };
      const res = await userAPI.upsertMySocial(payload);
      setMessage({ text: `Successfully saved: ${res.link || res.value}`, type: 'success' });
      closeDialog();
    } catch (err) {
      setMessage({ text: `Error: ${err?.message || 'Failed to save'}`, type: 'error' });
    } finally {
      setUpdateSocialLoading(false);
    }
  };

  const handleEdit = (social) => {
    setPlatform(social.platform);
    setValue(social.value || '');
    setVisibility(social.visibility || 'friends');
    openDialog();
  };

  const handleRemove = async (social) => {
    setSocialToRemove(social);
    setShowConfirmDialog(true);
  };

  const confirmRemove = async () => {
    if (!socialToRemove) return;

    try {
      await userAPI.deleteMySocial(socialToRemove.platform);
      setMessage({ text: `Successfully removed ${socialToRemove.platform} social`, type: 'success' });
    } catch (err) {
      setMessage({ text: `Error: ${err?.message || 'Failed to remove social'}`, type: 'error' });
    } finally {
      setSocialToRemove(null);
      setShowConfirmDialog(false);
    }
  };

  const cancelRemove = () => {
    setSocialToRemove(null);
    setShowConfirmDialog(false);
  };

  return (
    <div data-tour="social-settings">
      <h3>Social Settings</h3>
      
      {/* Existing socials list */}
      <div style={{ marginBottom: 16 }}>
        {Array.isArray(socials) && socials.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {socials.map((s, idx) => {
              const isTelegram = s.platform === 'telegram';
              const icon = isTelegram ? '✈️' : '📱';
              const name = isTelegram ? 'Telegram' : 'Signal';
              const canOpen = !!s.link;
              return (
                <div key={idx} style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 8,
                  border: '1px solid var(--color-border)', 
                  borderRadius: 6, 
                  padding: '12px'
                }}>
                  {/* Main content row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span aria-hidden>{icon}</span>
                    <strong>{name}</strong>
                    <span style={{ opacity: 0.85 }}>{s.value}</span>
                    <span style={{
                      marginLeft: 'auto',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 12,
                      border: '1px solid var(--color-border)'
                    }}>{s.visibility}</span>
                  </div>
                  
                  {/* Buttons row */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 8, 
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start'
                  }}>
                    <button 
                      className="btn" 
                      style={{ padding: '6px 12px', fontSize: '14px' }} 
                      disabled={!canOpen} 
                      onClick={() => canOpen && window.open(s.link, '_blank')}
                    >
                      Open
                    </button>
                    <button 
                      className="btn" 
                      style={{ padding: '6px 12px', fontSize: '14px' }} 
                      onClick={() => handleEdit(s)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn" 
                      style={{ padding: '6px 12px', fontSize: '14px' }} 
                      onClick={() => handleRemove(s)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ color: 'var(--color-text-muted)' }}>No socials added yet.</div>
        )}
      </div>

      {/* Add button */}
      <button 
        className="btn" 
        style={{ backgroundColor: '#007bff', color: 'white', border: 'none' }}
        onClick={openDialog}
      >
        Add
      </button>

      {/* Message display */}
      {message.text && (
        <div style={{
          marginTop: 15,
          padding: '10px',
          borderRadius: 6,
          color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
          backgroundColor: message.type === 'success' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
          border: `1px solid ${message.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Dialog */}
      {showDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            padding: 24,
            maxWidth: 500,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px var(--color-shadow)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 20, color: 'var(--color-text)' }}>Add Social</h3>
            
            <form onSubmit={onSubmit}>
              <div className="form-row" style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                <label style={{ minWidth: 90, color: 'var(--color-text)', fontWeight: 'bold' }}>Platform</label>
                <select 
                  value={platform} 
                  onChange={(e) => setPlatform(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text)'
                  }}
                >
                  <option value="telegram">Telegram</option>
                  <option value="signal">Signal</option>
                </select>
              </div>

              <div className="form-row" style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                <label style={{ minWidth: 90, color: 'var(--color-text)', fontWeight: 'bold' }}>Value</label>
                <input
                  type="text"
                  value={value}
                  placeholder={placeholder}
                  onChange={(e) => setValue(e.target.value)}
                  style={{ 
                    flex: 1,
                    padding: '10px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text)'
                  }}
                />
              </div>

              <div className="form-row" style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                <label style={{ minWidth: 90, color: 'var(--color-text)', fontWeight: 'bold' }}>Visibility</label>
                <select 
                  value={visibility} 
                  onChange={(e) => setVisibility(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    fontSize: '16px',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text)'
                  }}
                >
                  {VISIBILITIES.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 20 }}>
                <strong style={{ color: 'var(--color-text)' }}>Preview:</strong>{' '}
                {preview.error ? (
                  <span style={{ color: 'var(--color-danger)' }}>{preview.error}</span>
                ) : (
                  <a href={preview.link || '#'} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>
                    {preview.link || '—'}
                  </a>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="btn" 
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none' }}
                  onClick={closeDialog}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn" 
                  style={{ backgroundColor: '#007bff', color: 'white', border: 'none' }}
                  disabled={updateSocialLoading}
                >
                  {updateSocialLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && socialToRemove && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            padding: 24,
            maxWidth: 400,
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 12px var(--color-shadow)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 15, color: 'var(--color-text)' }}>Confirm Removal</h3>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: 20 }}>
              Are you sure you want to remove your {socialToRemove.platform} social? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button 
                className="btn" 
                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}
                onClick={confirmRemove}
              >
                Remove
              </button>
              <button 
                className="btn" 
                style={{ backgroundColor: '#6c757d', color: 'white', border: 'none' }}
                onClick={cancelRemove}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialSettings;


